import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  Plus,
  Search,
  Info,
  Pencil,
  Copy,
  Upload,
  Trash2,
  Eye,
  MoreVertical,
  IndianRupee,
  Ban,
  Users,
  Download,
  FileJson,
  ChevronLeft,
} from "lucide-react";
import {
  createTemplate,
  getTemplates,
  getTemplateComponents,
  getComponents,
  addTemplateComponent,
  updateTemplate,
  deleteTemplate,
  listEmployeeSalaryStructures,
} from "../SalaryApi";
import { useToast } from "../../Context/ToastContext";
import ConfirmDialog from "../../../components/ui/ConfirmDialog";
import "../../../styles/payroll-setup.css";
import {
  SAMPLE_STRUCTURE,
  buildStructureExport,
  downloadJson,
  parseStructureImportFile,
  matchImportComponents,
  toAddComponentPayload,
} from "./structureImportExport";
import {
  startSalaryTemplatePipeline,
  pipelineStepPath,
  PIPELINE_STEPS,
  isPipelineActive,
  parsePipelineStep,
} from "./enterprise/components/salaryTemplatePipeline";
import TemplateEmployeeManageModal from "./enterprise/components/TemplateEmployeeManageModal";
import SalaryStructurePipelineHost from "./enterprise/components/SalaryStructurePipelineHost";
import { formatInr } from "./enterprise/components/pipelineCtcUtils";
import { useEmployees } from "../../../hooks/useEmployees";
import { findEmployeeById } from "../../../components/common/employeeUtils";
import { PAYROLL_SETUP_TABS } from "./payrollSetupNav";
import { markSalaryStructuresConfigured } from "../../../services/markSalaryStructuresConfigured";
import { useSetupProgress } from "../../Context/SetupProgressContext";


function structureCode(t) {
  if (t?.code) return String(t.code).toUpperCase();
  const id = String(t?.template_id || "");
  return id ? id.replace(/-/g, "").slice(0, 8).toUpperCase() : "—";
}

/** Employee-side statutory / deduction codes (aligned with backend payroll_component_bucket). */
const KNOWN_EMPLOYEE_DEDUCTION_CODES = new Set([
  "NPS",
  "VPF",
  "INSURANCE",
  "INSURANCE_DED",
  "EMP_INSURANCE",
  "MEDICAL_INSURANCE",
  "PF",
  "PF_EMPLOYEE",
  "EMPLOYEE_PF",
  "EPF",
  "EPF_EE",
  "ESI",
  "ESI_EMPLOYEE",
  "EMPLOYEE_ESI",
  "PT",
  "PROF_TAX",
  "TDS",
  "LWF",
  "LOSS_OF_PAY",
  "LOP",
  "LOAN_RECOVERY",
  "ADVANCE",
  "ADVANCE_TAKEN",
]);

const KNOWN_EMPLOYER_CONTRIBUTION_CODES = new Set([
  "PF_EMPLOYER",
  "EMPLOYER_PF",
  "EPF_ER",
  "ESI_EMPLOYER",
  "EMPLOYER_ESI",
  "ESI_ER",
]);

function normCompCode(value) {
  return String(value || "")
    .trim()
    .toUpperCase()
    .replace(/[\s-]+/g, "_");
}

/**
 * Resolve display bucket for a master component.
 * Legacy /salary/components returns `component_type`; v2 uses `component_category`.
 * Defaulting unknown to earning caused Employee ESI etc. to show under Earnings with 0 deductions.
 */
function resolveComponentBucket(comp) {
  if (!comp) return "earning";

  const code = normCompCode(comp.code || comp.system_code);
  if (KNOWN_EMPLOYER_CONTRIBUTION_CODES.has(code)) return "contribution";
  if (KNOWN_EMPLOYEE_DEDUCTION_CODES.has(code)) return "deduction";

  const meta = comp.meta || {};
  const display = String(
    meta.payroll_display_category || meta.display_category || ""
  ).toLowerCase();
  const raw = String(
    display ||
      comp.component_category ||
      comp.component_type ||
      comp.category ||
      comp.type ||
      ""
  )
    .trim()
    .toLowerCase();

  if (
    raw === "employer_contribution" ||
    raw === "contribution" ||
    raw === "employer"
  ) {
    return "contribution";
  }
  if (
    raw === "deduction" ||
    raw === "deductions" ||
    raw === "statutory" ||
    raw === "benefit" ||
    raw === "loan" ||
    raw === "reimbursement"
  ) {
    // Benefits / statutory still reduce employee net → show under Deductions in structure preview.
    return "deduction";
  }

  // Name heuristics when master data is miscategorised as earning
  const name = String(comp.name || "").toLowerCase();
  if (
    /\b(employee\s+)?(esi|epf|pf|pt|tds|lwf|vpf|nps)\b/.test(name) ||
    /\b(loan|advance|recovery|deduction)\b/.test(name)
  ) {
    if (!/\bemployer\b/.test(name)) return "deduction";
  }
  if (/\bemployer\b/.test(name) && /\b(pf|esi|epf|contribution)\b/.test(name)) {
    return "contribution";
  }

  return "earning";
}

function shortName(name) {
  const n = String(name || "");
  if (n.length <= 22) return n;
  return `${n.slice(0, 20)}…`;
}

function ComponentTags({ names, max = 5 }) {
  if (!names?.length) return <span className="ps-comp-code">No components</span>;
  const shown = names.slice(0, max);
  const extra = names.length - shown.length;
  return (
    <div className="ps-comp-tags">
      {shown.map((n) => (
        <span key={n} className="ps-comp-tag">{n}</span>
      ))}
      {extra > 0 ? <span className="ps-comp-tag ps-comp-tag-more">+{extra} more</span> : null}
    </div>
  );
}

function getTemplateStatus(t) {
  const raw = String(t?.status || t?.meta?.status || "").trim().toLowerCase();
  if (raw === "draft" || raw === "inactive" || raw === "active") return raw;
  if (t?.is_active === false) return "inactive";
  return "active";
}

const STATUS_OPTIONS = [
  { value: "active", label: "Active" },
  { value: "draft", label: "Draft" },
  { value: "inactive", label: "Inactive" },
];

function StatusBadge({ status, onClick, open, children }) {
  const s = status || "active";
  const label = STATUS_OPTIONS.find((o) => o.value === s)?.label || "Active";
  return (
    <div className="ps-ss-status-wrap">
      <button
        type="button"
        className={`ps-ss-status ps-ss-status-${s}${onClick ? " ps-ss-status-btn" : ""}`}
        onClick={onClick}
        title={onClick ? "Change status" : undefined}
        aria-haspopup={onClick ? "menu" : undefined}
        aria-expanded={open || undefined}
      >
        <span className="ps-ss-status-dot" />
        {label}
      </button>
      {children}
    </div>
  );
}

function StatusMenu({ status, onSelect, flipUp }) {
  return (
    <div className={`ps-ss-status-menu${flipUp ? " ps-ss-status-menu-up" : ""}`} role="menu">
      <div className="ps-ss-status-menu-title">Change Status</div>
      {STATUS_OPTIONS.map((opt) => {
        const selected = status === opt.value;
        return (
          <button
            key={opt.value}
            type="button"
            role="menuitemradio"
            aria-checked={selected}
            className={`ps-ss-status-option${selected ? " is-selected" : ""}`}
            onClick={() => onSelect(opt.value)}
          >
            <span className={`ps-ss-status-radio${selected ? " is-on" : ""}`} />
            <span className={`ps-ss-status-option-label ps-ss-status-tone-${opt.value}`}>
              {opt.label}
            </span>
          </button>
        );
      })}
    </div>
  );
}

function CategoryCard({ tone, title, count, icon, names, emptyHint, emptyAction, onEmptyAction, max = 4 }) {
  const shown = names.slice(0, max);
  const extra = names.length - shown.length;
  return (
    <div className={`ps-ss-cat-card ps-ss-cat-${tone}`}>
      <div className="ps-ss-cat-head">
        <span className="ps-ss-cat-icon">{icon}</span>
        <div>
          <div className="ps-ss-cat-label">{title}</div>
          <div className="ps-ss-cat-count">{count}</div>
        </div>
      </div>
      {names.length === 0 ? (
        <div className="ps-ss-cat-empty">
          <span>{emptyHint}</span>
          {emptyAction ? (
            <button type="button" className="ps-ss-cat-link" onClick={onEmptyAction}>
              {emptyAction}
            </button>
          ) : null}
        </div>
      ) : (
        <ul className="ps-ss-cat-list">
          {shown.map((n) => (
            <li key={n}>{shortName(n)}</li>
          ))}
          {extra > 0 ? <li className="ps-ss-cat-more">+{extra} more</li> : null}
        </ul>
      )}
    </div>
  );
}

function isActiveEmployee(emp) {
  if (!emp) return false;
  if (emp.is_active === false) return false;
  const status = String(emp.employment_status || emp.status || "").toLowerCase();
  if (status === "inactive" || status === "terminated" || status === "left") return false;
  return true;
}

function ctcPackageSummary(rows) {
  const withCtc = (rows || []).filter((r) => r.ctc != null && r.ctc !== "");
  if (!withCtc.length) return { kind: "none" };
  const values = [...new Set(withCtc.map((r) => Number(r.ctc)).filter((n) => Number.isFinite(n)))];
  if (values.length === 1) {
    const annual = values[0];
    return { kind: "single", annual, monthly: Math.round(annual / 12), count: withCtc.length };
  }
  return { kind: "variable", count: withCtc.length };
}

export default function PayrollStructuresTab() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const pipelineStep = parsePipelineStep(searchParams);
  const inPipeline = isPipelineActive() && pipelineStep != null;
  const toast = useToast();
  const { employees } = useEmployees();
  const { refetch: refetchSetupProgress } = useSetupProgress() || {};
  const [submitLoading, setSubmitLoading] = useState(false);
  const prevSetupTab = PAYROLL_SETUP_TABS[PAYROLL_SETUP_TABS.findIndex((t) => t.id === "structures") - 1];

  const [templates, setTemplates] = useState([]);
  const [masterComponents, setMasterComponents] = useState([]);
  const [mappingsByTemplate, setMappingsByTemplate] = useState({});
  const [usageByTemplate, setUsageByTemplate] = useState({});
  const [assignmentsByTemplate, setAssignmentsByTemplate] = useState({});
  const [manageTemplate, setManageTemplate] = useState(null);
  const [loading, setLoading] = useState(true);
  const [previewLoading, setPreviewLoading] = useState(false);

  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [selectedId, setSelectedId] = useState(null);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [duplicatingId, setDuplicatingId] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [renameTarget, setRenameTarget] = useState(null);
  const [renameName, setRenameName] = useState("");
  const [renameDesc, setRenameDesc] = useState("");
  const [renaming, setRenaming] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [importName, setImportName] = useState("");
  const [importDesc, setImportDesc] = useState("");
  const [importRows, setImportRows] = useState([]);
  const [importMissing, setImportMissing] = useState([]);
  const [importFileName, setImportFileName] = useState("");
  const [importError, setImportError] = useState("");
  const [importing, setImporting] = useState(false);
  const [menuOpenId, setMenuOpenId] = useState(null);
  const [menuFlipUp, setMenuFlipUp] = useState(false);
  const [statusMenuId, setStatusMenuId] = useState(null);
  const [statusMenuFlipUp, setStatusMenuFlipUp] = useState(false);
  const [statusUpdatingId, setStatusUpdatingId] = useState(null);
  const menuRef = useRef(null);
  const statusMenuRef = useRef(null);
  const importFileRef = useRef(null);

  const componentById = useMemo(() => {
    const map = new Map();
    masterComponents.forEach((c) => map.set(c.component_id, c));
    return map;
  }, [masterComponents]);

  const resolveName = useCallback(
    (componentId) => componentById.get(componentId)?.name || componentId,
    [componentById]
  );

  const resolveBucket = useCallback(
    (componentId) => resolveComponentBucket(componentById.get(componentId)),
    [componentById]
  );

  const loadList = useCallback(async () => {
    try {
      setLoading(true);
      const [tpls, comps, assignments] = await Promise.all([
        getTemplates(),
        getComponents(),
        listEmployeeSalaryStructures().catch(() => []),
      ]);
      const list = Array.isArray(tpls) ? tpls : [];
      setTemplates(list);
      setMasterComponents(Array.isArray(comps) ? comps : []);
      if (list.length > 0) {
        setSelectedId((prev) => (prev && list.some((t) => t.template_id === prev) ? prev : list[0].template_id));
      } else {
        setSelectedId(null);
      }

      const usage = {};
      const byTemplate = {};
      (Array.isArray(assignments) ? assignments : []).forEach((row) => {
        const tid = row.template_id || row.salary_template_id;
        if (!tid) return;
        if (!usage[tid]) usage[tid] = { employees: new Set(), active: 0, inactive: 0 };
        if (!byTemplate[tid]) byTemplate[tid] = [];
        byTemplate[tid].push(row);
        if (row.employee_id) usage[tid].employees.add(String(row.employee_id));
      });
      const usageCounts = {};
      Object.entries(usage).forEach(([tid, u]) => {
        let active = 0;
        let inactive = 0;
        u.employees.forEach((eid) => {
          const emp = findEmployeeById(employees, eid);
          if (isActiveEmployee(emp)) active += 1;
          else inactive += 1;
        });
        usageCounts[tid] = { employees: u.employees.size, active, inactive };
      });
      setUsageByTemplate(usageCounts);
      setAssignmentsByTemplate(byTemplate);

      const pairs = await Promise.all(
        list.map(async (t) => {
          try {
            const rows = await getTemplateComponents(t.template_id);
            return [t.template_id, Array.isArray(rows) ? rows : []];
          } catch {
            return [t.template_id, []];
          }
        })
      );
      setMappingsByTemplate(Object.fromEntries(pairs));
    } catch {
      setTemplates([]);
      toast.error("Failed to load salary structures");
    } finally {
      setLoading(false);
    }
  }, [toast, employees]);

  useEffect(() => {
    loadList();
    // intentionally once on mount; reload via create/duplicate handlers
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!selectedId) return undefined;
    let cancelled = false;
    async function ensurePreview() {
      if (mappingsByTemplate[selectedId]) return;
      try {
        setPreviewLoading(true);
        const rows = await getTemplateComponents(selectedId);
        if (cancelled) return;
        setMappingsByTemplate((prev) => ({ ...prev, [selectedId]: Array.isArray(rows) ? rows : [] }));
      } catch {
        if (!cancelled) setMappingsByTemplate((prev) => ({ ...prev, [selectedId]: [] }));
      } finally {
        if (!cancelled) setPreviewLoading(false);
      }
    }
    ensurePreview();
    return () => {
      cancelled = true;
    };
  }, [selectedId, mappingsByTemplate]);

  useEffect(() => {
    function onDocClick(e) {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        setMenuOpenId(null);
        setMenuFlipUp(false);
      }
      if (statusMenuRef.current && !statusMenuRef.current.contains(e.target)) {
        setStatusMenuId(null);
        setStatusMenuFlipUp(false);
      }
    }
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, []);

  const filtered = useMemo(() => {
    let list = templates;
    const q = search.trim().toLowerCase();
    if (q) {
      list = list.filter((t) => {
        const maps = mappingsByTemplate[t.template_id] || [];
        const compNames = maps.map((m) => resolveName(m.component_id)).join(" ");
        return `${t.name} ${t.description || ""} ${structureCode(t)} ${compNames}`.toLowerCase().includes(q);
      });
    }
    if (statusFilter === "active") list = list.filter((t) => getTemplateStatus(t) === "active");
    else if (statusFilter === "draft") list = list.filter((t) => getTemplateStatus(t) === "draft");
    else if (statusFilter === "inactive") list = list.filter((t) => getTemplateStatus(t) === "inactive");
    return list;
  }, [templates, search, statusFilter, mappingsByTemplate, resolveName]);

  useEffect(() => {
    setPage(1);
  }, [search, statusFilter, pageSize]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));
  const pageRows = filtered.slice((page - 1) * pageSize, page * pageSize);

  const selected = templates.find((t) => t.template_id === selectedId) || filtered[0] || null;

  const selectedMappings = useMemo(() => {
    const id = selected?.template_id;
    if (!id) return [];
    return mappingsByTemplate[id] || [];
  }, [selected, mappingsByTemplate]);

  const bucketCounts = useCallback(
    (maps) => {
      let earnings = 0;
      let deductions = 0;
      let contributions = 0;
      const earningNames = [];
      const deductionNames = [];
      const contributionNames = [];
      maps.forEach((m) => {
        const name = resolveName(m.component_id);
        const bucket = resolveBucket(m.component_id);
        if (bucket === "contribution") {
          contributions += 1;
          contributionNames.push(name);
        } else if (bucket === "deduction") {
          deductions += 1;
          deductionNames.push(name);
        } else {
          earnings += 1;
          earningNames.push(name);
        }
      });
      return { earnings, deductions, contributions, earningNames, deductionNames, contributionNames, total: maps.length };
    },
    [resolveName, resolveBucket]
  );

  const selectedBuckets = useMemo(
    () => bucketCounts(selectedMappings),
    [selectedMappings, bucketCounts]
  );

  const structuresReady = templates.length > 0 && selectedBuckets.total > 0;

  async function submitStructuresSetup() {
    if (!templates.length) {
      toast.error("Create at least one salary structure before submitting.");
      return;
    }
    if (!selectedBuckets.total) {
      toast.error("Add components to your structure in Structure Builder, then submit again.");
      return;
    }
    try {
      setSubmitLoading(true);
      await markSalaryStructuresConfigured();
      await refetchSetupProgress?.();
      toast.success("Salary structures saved. Continue with employee salary assignment.");
      navigate("/employeesalarystructure");
    } catch (e) {
      toast.error(e?.message || "Could not save salary structures setup.");
    } finally {
      setSubmitLoading(false);
    }
  }

  function enrichedRow(t) {
    const maps = mappingsByTemplate[t.template_id] || [];
    const names = maps.map((m) => resolveName(m.component_id));
    const buckets = bucketCounts(maps);
    return { maps, names, ...buckets };
  }

  async function handleDuplicate(t) {
    if (!t?.template_id || duplicatingId) return;
    try {
      setDuplicatingId(t.template_id);
      const created = await createTemplate({
        name: `${t.name} (Copy)`,
        description: t.description || null,
      });
      const maps = mappingsByTemplate[t.template_id] || (await getTemplateComponents(t.template_id));
      for (const m of maps) {
        // eslint-disable-next-line no-await-in-loop
        await addTemplateComponent({
          template_id: created.template_id,
          component_id: m.component_id,
          calculation_source: m.calculation_source || "fixed_amount",
          amount: m.amount ?? undefined,
          percentage: m.percentage ?? undefined,
          percentage_of: m.percentage_of ?? undefined,
          reference: m.reference ?? undefined,
        });
      }
      toast.success("Structure duplicated.");
      await loadList();
      setSelectedId(created.template_id);
    } catch (err) {
      toast.error(err?.detail || err?.message || "Failed to duplicate structure");
    } finally {
      setDuplicatingId(null);
    }
  }

  function openRename(t) {
    setMenuOpenId(null);
    setRenameTarget(t);
    setRenameName(t?.name || "");
    setRenameDesc(t?.description || "");
  }

  async function confirmRename() {
    if (!renameTarget?.template_id) return;
    const name = renameName.trim();
    if (!name) {
      toast.error("Template name is required");
      return;
    }
    try {
      setRenaming(true);
      await updateTemplate(renameTarget.template_id, {
        name,
        description: renameDesc.trim() || null,
      });
      toast.success("Structure name updated.");
      const id = renameTarget.template_id;
      setRenameTarget(null);
      await loadList();
      setSelectedId(id);
    } catch (err) {
      const detail = err?.detail || err?.message;
      toast.error(typeof detail === "string" ? detail : "Failed to update structure name");
    } finally {
      setRenaming(false);
    }
  }

  async function confirmDelete() {
    if (!deleteTarget?.template_id) return;
    try {
      setDeleting(true);
      await deleteTemplate(deleteTarget.template_id);
      toast.success("Salary structure deleted.");
      const deletedId = deleteTarget.template_id;
      setDeleteTarget(null);
      await loadList();
      setSelectedId((prev) => (prev === deletedId ? null : prev));
    } catch (err) {
      const detail = err?.response?.data?.detail || err?.detail || err?.message;
      toast.error(typeof detail === "string" ? detail : "Failed to delete structure");
    } finally {
      setDeleting(false);
    }
  }

  const pageNumbers = useMemo(() => {
    const pages = [];
    const maxShown = 5;
    let start = Math.max(1, page - Math.floor(maxShown / 2));
    let end = Math.min(totalPages, start + maxShown - 1);
    start = Math.max(1, end - maxShown + 1);
    for (let i = start; i <= end; i += 1) pages.push(i);
    return pages;
  }, [page, totalPages]);

  function toggleRowMenu(templateId, event) {
    setStatusMenuId(null);
    if (menuOpenId === templateId) {
      setMenuOpenId(null);
      setMenuFlipUp(false);
      return;
    }
    const btn = event.currentTarget;
    const rect = btn.getBoundingClientRect();
    const menuHeight = 140;
    const spaceBelow = window.innerHeight - rect.bottom;
    const spaceAbove = rect.top;
    setMenuFlipUp(spaceBelow < menuHeight && spaceAbove > spaceBelow);
    setMenuOpenId(templateId);
  }

  function toggleStatusMenu(templateId, event) {
    event.stopPropagation();
    setMenuOpenId(null);
    if (statusMenuId === templateId) {
      setStatusMenuId(null);
      setStatusMenuFlipUp(false);
      return;
    }
    const btn = event.currentTarget;
    const rect = btn.getBoundingClientRect();
    const menuHeight = 160;
    const spaceBelow = window.innerHeight - rect.bottom;
    const spaceAbove = rect.top;
    setStatusMenuFlipUp(spaceBelow < menuHeight && spaceAbove > spaceBelow);
    setStatusMenuId(templateId);
  }

  async function handleStatusChange(t, nextStatus) {
    if (!t?.template_id || statusUpdatingId) return;
    const current = getTemplateStatus(t);
    if (current === nextStatus) {
      setStatusMenuId(null);
      return;
    }
    try {
      setStatusUpdatingId(t.template_id);
      const updated = await updateTemplate(t.template_id, { status: nextStatus });
      setTemplates((prev) =>
        prev.map((row) =>
          row.template_id === t.template_id
            ? {
                ...row,
                ...updated,
                status: nextStatus,
                meta: { ...(row.meta || {}), ...(updated?.meta || {}), status: nextStatus },
              }
            : row
        )
      );
      setStatusMenuId(null);
      toast.success(
        `Status set to ${STATUS_OPTIONS.find((o) => o.value === nextStatus)?.label || nextStatus}.`
      );
    } catch (err) {
      const detail = err?.detail || err?.message;
      toast.error(typeof detail === "string" ? detail : "Failed to update status");
    } finally {
      setStatusUpdatingId(null);
    }
  }

  function openBuilder(id) {
    navigate(`/salary/templates/${id}`);
  }

  function resetImportForm() {
    setImportName("");
    setImportDesc("");
    setImportRows([]);
    setImportMissing([]);
    setImportFileName("");
    setImportError("");
    if (importFileRef.current) importFileRef.current.value = "";
  }

  function openImportModal() {
    resetImportForm();
    setImportOpen(true);
  }

  function applyParsedImport(parsed, fileLabel) {
    const { matched, missing, rows } = matchImportComponents(parsed, masterComponents);
    setImportName(parsed.name);
    setImportDesc(parsed.description || "");
    setImportRows(rows);
    setImportMissing(missing);
    setImportFileName(fileLabel || "");
    setImportError(
      matched.length === 0
        ? "None of the components in this file match your master component list. Create those components in 3.2 first, or export from an existing structure."
        : ""
    );
  }

  async function handleImportFile(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const text = await file.text();
      const parsed = parseStructureImportFile(text);
      applyParsedImport(parsed, file.name);
    } catch (err) {
      resetImportForm();
      setImportError(err?.message || "Failed to read import file");
    }
  }

  function loadSampleImport() {
    applyParsedImport(SAMPLE_STRUCTURE, "sample-structure.json");
  }

  function downloadSample() {
    downloadJson("salary-structure-sample.json", SAMPLE_STRUCTURE);
  }

  async function handleExport(t) {
    setMenuOpenId(null);
    try {
      const maps = mappingsByTemplate[t.template_id] || (await getTemplateComponents(t.template_id));
      const payload = buildStructureExport(t, maps, (id) => componentById.get(id));
      const safe = String(t.name || "structure")
        .replace(/[^\w-]+/g, "-")
        .replace(/-+/g, "-")
        .slice(0, 40);
      downloadJson(`${safe || "structure"}-export.json`, payload);
      toast.success("Structure exported as JSON.");
    } catch (err) {
      toast.error(err?.message || "Failed to export structure");
    }
  }

  async function confirmImport() {
    const name = importName.trim();
    if (!name) {
      setImportError("Structure name is required");
      return;
    }
    const okRows = importRows.filter((r) => r.component_id);
    if (!okRows.length) {
      setImportError("No matching components to import");
      return;
    }
    try {
      setImporting(true);
      setImportError("");
      const created = await createTemplate({
        name,
        description: importDesc.trim() || null,
      });
      let added = 0;
      const failed = [];
      for (const row of okRows) {
        try {
          // eslint-disable-next-line no-await-in-loop
          await addTemplateComponent(toAddComponentPayload(created.template_id, row));
          added += 1;
        } catch (err) {
          const detail = err?.detail || err?.message || "mapping failed";
          failed.push(`${row.resolved_name || row.name || row.code}: ${typeof detail === "string" ? detail : "failed"}`);
        }
      }
      setImportOpen(false);
      resetImportForm();
      await loadList();
      setSelectedId(created.template_id);
      if (failed.length) {
        toast.error(`Imported with ${added} components; ${failed.length} skipped.`);
      } else {
        toast.success(`Imported “${name}” with ${added} component${added === 1 ? "" : "s"}.`);
      }
    } catch (err) {
      const detail = err?.detail || err?.message;
      setImportError(typeof detail === "string" ? detail : "Failed to import structure");
    } finally {
      setImporting(false);
    }
  }

  if (inPipeline) {
    return <SalaryStructurePipelineHost />;
  }

  return (
    <div className="ps-structures">
      <div className="ps-toolbar ps-ss-toolbar">
        <div className="ps-search">
          <Search size={15} />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by name, code or component…"
            aria-label="Search structures"
          />
        </div>
        <select
          className="ps-select"
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          aria-label="Status filter"
        >
          <option value="all">All Status</option>
          <option value="active">Active</option>
          <option value="draft">Draft</option>
          <option value="inactive">Inactive</option>
        </select>
        <div className="ps-ss-toolbar-actions">
          <button
            type="button"
            className="bw-btn bw-btn-secondary"
            onClick={openImportModal}
            title="Import a structure from a JSON file"
          >
            <Upload size={15} /> Import
          </button>
          <button
            type="button"
            className="bw-btn bw-btn-primary ps-ss-create-btn"
            onClick={() => {
              startSalaryTemplatePipeline();
              navigate(pipelineStepPath(PIPELINE_STEPS.template));
            }}
          >
            <Plus size={15} /> Create Salary Structure
          </button>
        </div>
      </div>

      <div className="ps-info-banner">
        <Info size={16} />
        <span>
          Salary structures are templates that define the components and formulas used to calculate employee salaries.
        </span>
      </div>

      <div className="ps-table-block">
        <div className="ps-table-wrap">
          <table className="ps-table ps-ss-table">
            <thead>
              <tr>
                <th>Structure</th>
                <th>Components</th>
                <th>Employees</th>
                <th>Gross package</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={6} className="ps-table-empty">Loading structures…</td></tr>
              ) : pageRows.length === 0 ? (
                <tr>
                  <td colSpan={6} className="ps-table-empty">
                    No salary structures yet. Create one to get started.
                  </td>
                </tr>
              ) : (
                pageRows.map((t) => {
                  const { names, total, earnings, deductions, contributions } = enrichedRow(t);
                  const rowStatus = getTemplateStatus(t);
                  const isSelected = selected?.template_id === t.template_id;
                  const isDefault = Boolean(t.is_default);
                  const statusOpen = statusMenuId === t.template_id;
                  const usage = usageByTemplate[t.template_id] || { employees: 0, active: 0, inactive: 0 };
                  const assignRows = assignmentsByTemplate[t.template_id] || [];
                  const ctcInfo = ctcPackageSummary(assignRows);
                  return (
                    <tr
                      key={t.template_id}
                      onClick={() => setSelectedId(t.template_id)}
                      className={isSelected ? "ps-row-selected" : undefined}
                    >
                      <td>
                        <div className="ps-ss-struct-cell">
                          <div className="ps-comp-name">{t.name}</div>
                          <div className="ps-ss-struct-meta">
                            <span className="ps-comp-code">{structureCode(t)}</span>
                            {isDefault ? <span className="ps-ss-default-badge">Default</span> : null}
                          </div>
                        </div>
                      </td>
                      <td>
                        <div className="ps-ss-total">
                          <span className="ps-ss-total-count">{total} Component{total === 1 ? "" : "s"}</span>
                          <ComponentTags names={names} max={3} />
                          <div className="ps-ss-total-breakdown">
                            <span><i className="ps-ss-dot ps-ss-dot-earn" />{earnings} Earnings</span>
                            <span><i className="ps-ss-dot ps-ss-dot-ded" />{deductions} Deductions</span>
                            <span><i className="ps-ss-dot ps-ss-dot-cont" />{contributions} Contributions</span>
                          </div>
                        </div>
                      </td>
                      <td>
                        <button
                          type="button"
                          className="ps-ss-emp-cell"
                          onClick={(e) => {
                            e.stopPropagation();
                            setManageTemplate(t);
                          }}
                        >
                          <span className="ps-ss-emp-count">
                            <Users size={14} aria-hidden />
                            {usage.employees} Assigned
                          </span>
                          {usage.employees > 0 ? (
                            <span className="ps-ss-emp-meta">
                              {usage.active} Active · {usage.inactive} Inactive
                            </span>
                          ) : null}
                          <span className="ps-ss-emp-link">
                            {usage.employees ? "View employees →" : "Assign employees →"}
                          </span>
                        </button>
                      </td>
                      <td>
                        <div className="ps-ss-ctc-cell">
                          {ctcInfo.kind === "single" ? (
                            <>
                              <strong>{formatInr(ctcInfo.annual)} / year</strong>
                              <span className="ps-ss-emp-meta">Monthly: {formatInr(ctcInfo.monthly)}</span>
                            </>
                          ) : ctcInfo.kind === "variable" ? (
                            <>
                              <strong>Variable</strong>
                              <span className="ps-ss-emp-meta">{ctcInfo.count} packages configured</span>
                            </>
                          ) : (
                            <>
                              <strong>Not configured</strong>
                              {usage.employees ? (
                                <span className="ps-ss-emp-meta">{usage.employees} employee packages</span>
                              ) : null}
                            </>
                          )}
                        </div>
                      </td>
                      <td onClick={(e) => e.stopPropagation()}>
                        <div
                          className={`ps-ss-status-cell${statusOpen ? " is-open" : ""}`}
                          ref={statusOpen ? statusMenuRef : null}
                        >
                          <StatusBadge
                            status={rowStatus}
                            open={statusOpen}
                            onClick={(e) => toggleStatusMenu(t.template_id, e)}
                          >
                            {statusOpen ? (
                              <StatusMenu
                                status={rowStatus}
                                flipUp={statusMenuFlipUp}
                                onSelect={(next) => handleStatusChange(t, next)}
                              />
                            ) : null}
                          </StatusBadge>
                          {statusUpdatingId === t.template_id ? (
                            <span className="ps-ss-status-saving">Saving…</span>
                          ) : null}
                        </div>
                      </td>
                      <td>
                        <div className="ps-row-actions" onClick={(e) => e.stopPropagation()}>
                          <button
                            type="button"
                            className="ps-act"
                            title="View"
                            onClick={() => {
                              setSelectedId(t.template_id);
                              openBuilder(t.template_id);
                            }}
                          >
                            <Eye size={15} />
                          </button>
                          <button
                            type="button"
                            className="ps-act"
                            title="Edit"
                            onClick={() => openBuilder(t.template_id)}
                          >
                            <Pencil size={15} />
                          </button>
                          <button
                            type="button"
                            className="ps-act"
                            title="Manage Employees"
                            onClick={() => setManageTemplate(t)}
                          >
                            <Users size={15} />
                          </button>
                          <button
                            type="button"
                            className="ps-act ps-act-muted"
                            title="Duplicate"
                            disabled={duplicatingId === t.template_id}
                            onClick={() => handleDuplicate(t)}
                          >
                            <Copy size={15} />
                          </button>
                          <div
                            className={`ps-ss-more-wrap${menuOpenId === t.template_id ? " ps-ss-more-open" : ""}`}
                            ref={menuOpenId === t.template_id ? menuRef : null}
                          >
                            <button
                              type="button"
                              className="ps-act ps-act-muted"
                              title="More"
                              aria-haspopup="menu"
                              aria-expanded={menuOpenId === t.template_id}
                              onClick={(e) => toggleRowMenu(t.template_id, e)}
                            >
                              <MoreVertical size={15} />
                            </button>
                            {menuOpenId === t.template_id ? (
                              <div
                                className={`ps-ss-more-menu${menuFlipUp ? " ps-ss-more-menu-up" : ""}`}
                                role="menu"
                              >
                                <button
                                  type="button"
                                  role="menuitem"
                                  className="ps-ss-more-item"
                                  onClick={() => openRename(t)}
                                >
                                  <Pencil size={14} /> Rename
                                </button>
                                <button
                                  type="button"
                                  role="menuitem"
                                  className="ps-ss-more-item"
                                  onClick={() => handleExport(t)}
                                >
                                  <Download size={14} /> Export JSON
                                </button>
                                <button
                                  type="button"
                                  role="menuitem"
                                  className="ps-ss-more-item ps-ss-more-danger"
                                  onClick={() => {
                                    setMenuOpenId(null);
                                    setDeleteTarget(t);
                                  }}
                                >
                                  <Trash2 size={14} /> Delete
                                </button>
                              </div>
                            ) : null}
                          </div>
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
        {!loading && filtered.length > 0 ? (
          <div className="ps-pagination">
            <span>
              Showing {(page - 1) * pageSize + 1} to {Math.min(page * pageSize, filtered.length)} of {filtered.length} entries
            </span>
            <div className="ps-page-controls">
              <select
                className="ps-select"
                value={pageSize}
                onChange={(e) => setPageSize(Number(e.target.value))}
                aria-label="Rows per page"
              >
                {[5, 10, 20].map((n) => (
                  <option key={n} value={n}>{n} per page</option>
                ))}
              </select>
              <button type="button" className="ps-page-btn" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>
                ‹
              </button>
              {pageNumbers.map((n) => (
                <button
                  key={n}
                  type="button"
                  className={`ps-page-btn${n === page ? " ps-page-btn-active" : ""}`}
                  onClick={() => setPage(n)}
                >
                  {n}
                </button>
              ))}
              <button type="button" className="ps-page-btn" disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)}>
                ›
              </button>
            </div>
          </div>
        ) : null}
      </div>

      {selected ? (
        <div className="ps-ss-preview-layout">
          <div className="ps-ss-preview-main">
            <div className="ps-ss-preview-head">
              <div>
                <h3 className="ps-preview-heading">Structure Preview: {selected.name}</h3>
                <p className="ps-ss-preview-sub">
                  {previewLoading
                    ? "Loading component breakdown…"
                    : `${selectedBuckets.total} components across earnings, deductions & contributions`}
                </p>
              </div>
              <button
                type="button"
                className="ps-ss-view-details"
                onClick={() => openBuilder(selected.template_id)}
              >
                View Full Details
              </button>
            </div>
            <div className="ps-ss-cat-grid">
              <CategoryCard
                tone="earn"
                title="Earnings"
                count={selectedBuckets.earnings}
                icon={<IndianRupee size={16} />}
                names={selectedBuckets.earningNames}
                emptyHint="No earnings mapped yet."
                emptyAction="Add Earnings →"
                onEmptyAction={() => openBuilder(selected.template_id)}
              />
              <CategoryCard
                tone="ded"
                title="Deductions"
                count={selectedBuckets.deductions}
                icon={<Ban size={16} />}
                names={selectedBuckets.deductionNames}
                emptyHint="No deductions mapped yet."
                emptyAction="Add Deductions →"
                onEmptyAction={() => openBuilder(selected.template_id)}
              />
              <CategoryCard
                tone="cont"
                title="Employer Contributions"
                count={selectedBuckets.contributions}
                icon={<Users size={16} />}
                names={selectedBuckets.contributionNames}
                emptyHint="No employer contributions"
                emptyAction="Add Contributions →"
                onEmptyAction={() => openBuilder(selected.template_id)}
              />
            </div>
          </div>
        </div>
      ) : null}

      <div className="ps-info-banner" style={{ marginTop: "1.25rem" }}>
        <Info size={16} />
        <span>
          Inactive salary structures cannot be assigned to new employees but will remain available for existing employee records.
        </span>
      </div>

      <div className="ps-setup-nav" role="navigation" aria-label="Payroll setup navigation">
        {prevSetupTab ? (
          <button type="button" className="bw-btn bw-btn-secondary" onClick={() => navigate(prevSetupTab.to)}>
            <ChevronLeft size={15} /> Back
          </button>
        ) : (
          <span />
        )}
        <button
          type="button"
          className="bw-btn bw-btn-primary"
          onClick={submitStructuresSetup}
          disabled={submitLoading || !structuresReady}
          title={!structuresReady ? "Create a structure with at least one component first" : undefined}
        >
          {submitLoading ? "Saving…" : "Submit"}
        </button>
      </div>

      <ConfirmDialog
        open={Boolean(deleteTarget)}
        title="Delete salary structure?"
        message={
          deleteTarget
            ? `"${deleteTarget.name}" and its component mappings will be permanently removed. Structures assigned to employees cannot be deleted.`
            : ""
        }
        confirmLabel="Delete"
        cancelLabel="Cancel"
        danger
        loading={deleting}
        onCancel={() => !deleting && setDeleteTarget(null)}
        onConfirm={confirmDelete}
      />

      {renameTarget ? (
        <div
          className="ps-modal-overlay"
          role="dialog"
          aria-modal="true"
          aria-labelledby="ps-ss-rename-title"
          onClick={() => !renaming && setRenameTarget(null)}
        >
          <div className="ps-modal" onClick={(e) => e.stopPropagation()}>
            <div className="ps-modal-head">
              <h3 id="ps-ss-rename-title">Edit structure name</h3>
              <button
                type="button"
                className="ps-act ps-act-muted"
                aria-label="Close"
                disabled={renaming}
                onClick={() => setRenameTarget(null)}
              >
                ×
              </button>
            </div>
            <div className="ps-modal-body">
              <div className="ps-field">
                <label htmlFor="ps-ss-rename-name">Structure name</label>
                <input
                  id="ps-ss-rename-name"
                  value={renameName}
                  onChange={(e) => setRenameName(e.target.value)}
                  placeholder="e.g. Standard Staff"
                  maxLength={150}
                  autoFocus
                  disabled={renaming}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      confirmRename();
                    }
                  }}
                />
              </div>
              <div className="ps-field" style={{ marginBottom: 0 }}>
                <label htmlFor="ps-ss-rename-desc">Description (optional)</label>
                <input
                  id="ps-ss-rename-desc"
                  value={renameDesc}
                  onChange={(e) => setRenameDesc(e.target.value)}
                  placeholder="Short description"
                  disabled={renaming}
                />
              </div>
            </div>
            <div className="ps-modal-foot">
              <button
                type="button"
                className="bw-btn bw-btn-secondary"
                disabled={renaming}
                onClick={() => setRenameTarget(null)}
              >
                Cancel
              </button>
              <button
                type="button"
                className="bw-btn bw-btn-primary"
                disabled={renaming || !renameName.trim()}
                onClick={confirmRename}
              >
                {renaming ? "Saving…" : "Save"}
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {importOpen ? (
        <div
          className="ps-modal-overlay"
          role="dialog"
          aria-modal="true"
          aria-labelledby="ps-ss-import-title"
          onClick={() => !importing && setImportOpen(false)}
        >
          <div className="ps-modal ps-ss-import-modal" onClick={(e) => e.stopPropagation()}>
            <div className="ps-modal-head">
              <h3 id="ps-ss-import-title">Import Salary Structure</h3>
              <button
                type="button"
                className="ps-act ps-act-muted"
                aria-label="Close"
                disabled={importing}
                onClick={() => !importing && setImportOpen(false)}
              >
                ×
              </button>
            </div>
            <div className="ps-modal-body">
              <p className="ps-ss-import-hint">
                Upload a JSON structure file (export one from ⋮ → Export JSON), or load the sample.
                Components are matched to your master list by <strong>code</strong>, then by name.
              </p>

              <div className="ps-ss-import-actions">
                <label className="bw-btn bw-btn-secondary ps-ss-file-btn">
                  <Upload size={15} /> Choose JSON file
                  <input
                    ref={importFileRef}
                    type="file"
                    accept=".json,application/json"
                    hidden
                    onChange={handleImportFile}
                    disabled={importing}
                  />
                </label>
                <button type="button" className="bw-btn bw-btn-secondary" onClick={loadSampleImport} disabled={importing}>
                  <FileJson size={15} /> Use sample
                </button>
                <button type="button" className="ps-used-in-link" onClick={downloadSample} disabled={importing}>
                  Download sample file
                </button>
              </div>

              {importFileName ? (
                <p className="ps-ss-import-file">File: {importFileName}</p>
              ) : null}

              <div className="ps-field">
                <label htmlFor="ps-ss-import-name">Structure name</label>
                <input
                  id="ps-ss-import-name"
                  value={importName}
                  onChange={(e) => setImportName(e.target.value)}
                  placeholder="Name for the new structure"
                  maxLength={150}
                  disabled={importing}
                />
              </div>
              <div className="ps-field">
                <label htmlFor="ps-ss-import-desc">Description (optional)</label>
                <input
                  id="ps-ss-import-desc"
                  value={importDesc}
                  onChange={(e) => setImportDesc(e.target.value)}
                  placeholder="Short description"
                  disabled={importing}
                />
              </div>

              {importRows.length > 0 ? (
                <div className="ps-ss-import-preview">
                  <div className="ps-ss-import-preview-head">
                    <span>
                      {importRows.filter((r) => r.status === "ok").length} of {importRows.length} components matched
                    </span>
                    {importMissing.length > 0 ? (
                      <span className="ps-ss-import-warn">{importMissing.length} missing in master list</span>
                    ) : null}
                  </div>
                  <div className="ps-table-wrap">
                    <table className="ps-table">
                      <thead>
                        <tr>
                          <th>Code / Name</th>
                          <th>Source</th>
                          <th>Match</th>
                        </tr>
                      </thead>
                      <tbody>
                        {importRows.map((row, idx) => (
                          <tr key={`${row.code || row.name || "row"}-${idx}`}>
                            <td>
                              <div className="ps-comp-name">{row.resolved_name || row.name || "—"}</div>
                              <div className="ps-comp-code">{row.resolved_code || row.code || "—"}</div>
                            </td>
                            <td>{row.calculation_source || "—"}</td>
                            <td>
                              <span className={row.status === "ok" ? "ps-ss-status ps-ss-status-active" : "ps-ss-status ps-ss-status-inactive"}>
                                <span className="ps-ss-status-dot" />
                                {row.status === "ok" ? "Matched" : "Missing"}
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              ) : null}

              {importError ? <div className="ps-ss-import-error">{importError}</div> : null}
            </div>
            <div className="ps-modal-foot">
              <button
                type="button"
                className="bw-btn bw-btn-secondary"
                disabled={importing}
                onClick={() => setImportOpen(false)}
              >
                Cancel
              </button>
              <button
                type="button"
                className="bw-btn bw-btn-primary"
                disabled={
                  importing ||
                  !importName.trim() ||
                  importRows.filter((r) => r.status === "ok").length === 0
                }
                onClick={confirmImport}
              >
                {importing ? "Importing…" : "Import Structure"}
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {manageTemplate ? (
        <TemplateEmployeeManageModal
          templateId={manageTemplate.template_id}
          templateName={manageTemplate.name}
          onClose={() => setManageTemplate(null)}
          onChanged={loadList}
        />
      ) : null}
    </div>
  );
}
